import type { ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useSesion } from './auth/sesion';
import MiCuenta from './paginas/MiCuenta';
import Sedes from './paginas/Sedes';
import ProyectoFicha from './paginas/ProyectoFicha';
import Entrar from './paginas/Entrar';
import Inmuebles from './paginas/Inmuebles';
import InmuebleFicha from './paginas/InmuebleFicha';
import Recorrido from './paginas/Recorrido';
import Catalogo from './paginas/Catalogo';
import Aliados from './paginas/Aliados';
import Propietarios from './paginas/Propietarios';
import Solicitudes from './paginas/Solicitudes';
import SolicitudFicha from './paginas/SolicitudFicha';
import Agenda from './paginas/Agenda';
import Proyectos from './paginas/Proyectos';
import Usuarios from './paginas/Usuarios';
import Roles from './paginas/Roles';
import Bitacora from './paginas/Bitacora';
import Informes from './paginas/Informes';
import { Cargando } from './ui/piezas';

/**
 * Una ruta que exige sesión y, si se pide, un permiso.
 *
 * El permiso se comprueba también aquí y no solo escondiendo el enlace del menú:
 * esconder una opción es cortesía, no seguridad, y la URL se puede escribir a
 * mano. La comprobación de verdad la hace la API en cada llamada.
 */
function Protegida({ permiso, children }: { permiso?: string; children: ReactNode }) {
  const { usuario, cargando, puede } = useSesion();
  const donde = useLocation();

  if (cargando) return <Cargando />;
  if (!usuario) return <Navigate to="/entrar" state={{ volverA: donde.pathname }} replace />;
  if (permiso && !puede(permiso)) {
    return (
      <div className="lienzo">
        <p className="aviso aviso--mal">
          Tu cuenta no tiene permiso para ver esta pantalla. Si crees que debería tenerlo,
          pídeselo a quien administra los usuarios.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

function Menu() {
  const { usuario, puede, salir } = useSesion();
  if (!usuario) return null;

  return (
    <nav className="barra">
      <div className="barra__marca">
        <img src="/favicon.svg" alt="" width="26" height="26" />
        <strong>Cordillera</strong>
      </div>

      <div className="menu">
        <span className="menu__grupo">Inventario</span>
        {puede('inmueble:leer') && <NavLink to="/inmuebles">Inmuebles</NavLink>}
        {puede('proyecto:leer') && <NavLink to="/proyectos">Proyectos</NavLink>}
        {puede('propietario:leer') && <NavLink to="/propietarios">Propietarios</NavLink>}

        <span className="menu__grupo">Comercial</span>
        {puede('lead:leer') && <NavLink to="/solicitudes">Solicitudes</NavLink>}
        {puede('cita:ver_agenda') && <NavLink to="/agenda">Agenda</NavLink>}
        {puede('reporte:comercial') && <NavLink to="/informes">Informes</NavLink>}

        <span className="menu__grupo">Configuración</span>
        {puede('sede:leer') && <NavLink to="/sedes">Oficinas</NavLink>}
        <NavLink to="/catalogo">Catálogo</NavLink>
        <NavLink to="/aliados">Aliados</NavLink>
        {puede('usuario:leer') && <NavLink to="/usuarios">Usuarios</NavLink>}
        {puede('rol:leer') && <NavLink to="/roles">Roles y permisos</NavLink>}
        {puede('auditoria:leer') && <NavLink to="/bitacora">Bitácora</NavLink>}
      </div>

      <div className="barra__pie">
        <div>
          {usuario.nombre} {usuario.apellido}
        </div>
        <div style={{ opacity: 0.7 }}>{usuario.sedeNombre}</div>
        {/* Sin permiso que la proteja: la cuenta propia es de todo el mundo. */}
        <NavLink to="/mi-cuenta">Mi cuenta</NavLink>
        <button className="plano" style={{ paddingLeft: 0 }} onClick={() => void salir()}>
          Salir
        </button>
      </div>
    </nav>
  );
}

export default function App() {
  const { usuario, cargando } = useSesion();

  if (cargando) {
    return (
      <div className="lienzo">
        <Cargando />
      </div>
    );
  }

  if (!usuario) {
    return (
      <Routes>
        <Route path="/entrar" element={<Entrar />} />
        <Route path="*" element={<Navigate to="/entrar" replace />} />
      </Routes>
    );
  }

  return (
    <div className="marco">
      <Menu />
      <main className="lienzo">
        <Routes>
          <Route path="/entrar" element={<Navigate to="/inmuebles" replace />} />
          <Route path="/mi-cuenta" element={<MiCuenta />} />
          <Route
            path="/sedes"
            element={
              <Protegida permiso="sede:leer">
                <Sedes />
              </Protegida>
            }
          />
          <Route
            path="/inmuebles"
            element={
              <Protegida permiso="inmueble:leer">
                <Inmuebles />
              </Protegida>
            }
          />
          <Route
            path="/inmuebles/nuevo"
            element={
              <Protegida permiso="inmueble:crear">
                <InmuebleFicha />
              </Protegida>
            }
          />
          <Route
            path="/inmuebles/:id"
            element={
              <Protegida permiso="inmueble:leer">
                <InmuebleFicha />
              </Protegida>
            }
          />
          {/* El recorrido va en su propia ruta y no dentro de la ficha, que ya
              es larga: armarlo es una sesion de trabajo aparte, con la
              panoramica ocupando media pantalla. */}
          <Route
            path="/inmuebles/:id/recorrido"
            element={
              <Protegida permiso="inmueble:leer">
                <Recorrido />
              </Protegida>
            }
          />
          <Route
            path="/proyectos"
            element={
              <Protegida permiso="proyecto:leer">
                <Proyectos />
              </Protegida>
            }
          />
          <Route
            path="/proyectos/nuevo"
            element={
              <Protegida permiso="proyecto:crear">
                <ProyectoFicha />
              </Protegida>
            }
          />
          <Route
            path="/proyectos/:id"
            element={
              <Protegida permiso="proyecto:leer">
                <ProyectoFicha />
              </Protegida>
            }
          />
          <Route
            path="/propietarios"
            element={
              <Protegida permiso="propietario:leer">
                <Propietarios />
              </Protegida>
            }
          />
          <Route
            path="/solicitudes"
            element={
              <Protegida permiso="lead:leer">
                <Solicitudes />
              </Protegida>
            }
          />
          <Route
            path="/solicitudes/:id"
            element={
              <Protegida permiso="lead:leer">
                <SolicitudFicha />
              </Protegida>
            }
          />
          <Route
            path="/agenda"
            element={
              <Protegida permiso="cita:ver_agenda">
                <Agenda />
              </Protegida>
            }
          />
          <Route
            path="/informes"
            element={
              <Protegida permiso="reporte:comercial">
                <Informes />
              </Protegida>
            }
          />
          <Route
            path="/catalogo"
            element={
              <Protegida>
                <Catalogo />
              </Protegida>
            }
          />
          <Route
            path="/aliados"
            element={
              <Protegida>
                <Aliados />
              </Protegida>
            }
          />
          <Route
            path="/usuarios"
            element={
              <Protegida permiso="usuario:leer">
                <Usuarios />
              </Protegida>
            }
          />
          <Route
            path="/roles"
            element={
              <Protegida permiso="rol:leer">
                <Roles />
              </Protegida>
            }
          />
          <Route
            path="/bitacora"
            element={
              <Protegida permiso="auditoria:leer">
                <Bitacora />
              </Protegida>
            }
          />
          <Route path="*" element={<Navigate to="/inmuebles" replace />} />
        </Routes>
      </main>
    </div>
  );
}
